import {Component, Inject, Input, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Data, Router} from '@angular/router';
import {TranslocoService} from '@ngneat/transloco';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {AbstractComponent, ConfirmationModalComponent, NavTabsComponent} from "@components";
import {
  EventFilter,
  EventItem,
  EventPaymentSettings,
  EventStatus,
  FetchEventByIdRequest, Lookups, Organiser,
  Race
} from "@model";
import {AuthenticationService, EventService, ExportService, RaceService, TOAST_STATE, ToastService} from "@services";
import {isEventBasicDataValid} from "@utility";
import {
  ORGANISER_RACE_ADD, ORGANISER_RACE_COPY,
  ORGANISER_RACE_EDIT,
  ORGANISER_RACE_EDIT_COPY,
  ORGANISER_RACE_MANAGEMENT
} from "src/app/app-routing.module";
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'app-admin-event-details',
  templateUrl: './admin-event-details.component.html',
  styleUrls: ['./admin-event-details.component.scss'],
  animations: [
    trigger('fadeInAnimation', [
      state('in', style({opacity: 1})),
      transition(':enter', [
        style({opacity: 0}),
        animate(1600)
      ])
    ])]
})

export class AdminEventDetailsComponent extends AbstractComponent implements OnInit {
  @Input('eventPaymentMethods') paymentMethods: Lookups[] = [];
  @Input('eventPaymentReminderMethods') paymentReminderMethods: Lookups[] = [];
  @Input('eventPaymentTypes') paymentTypes: Lookups[] = [];
  @Input() countries: Lookups[] = [];
  @Input() sports: Lookups[] = [];
  @Input() currencies: Lookups[] = [];
  @Input() eventTypes: Lookups[] = [];
  @Input() eventStatuses: Lookups[] = [];
  @Input() languages: Lookups[] = [];
  @Input() organisers: Organiser[] = [];
  @Input() userRole: String | undefined;

  filter: EventFilter = new EventFilter();
  currentEventItem: EventItem | undefined;
  currentPaymentSettings: EventPaymentSettings | undefined;
  componentType: EventDetailsComponentType = EventDetailsComponentType.ADD
  isEventInvalid: boolean = true;

  @ViewChild("nav") nav?: NavTabsComponent;

  constructor(
    private route: ActivatedRoute,
    public translocoService: TranslocoService,
    private router: Router,
    private raceService: RaceService,
    private eventService: EventService,
    private modalService: NgbModal,
    private toastService: ToastService,
    private authService: AuthenticationService,
    private exportService: ExportService,
    @Inject(DOCUMENT) private document: Document
  ) {
    super();
  }

  ngOnInit() {
    this.subscribeToRouteDataChange();
    this.subscriptions.push(this.eventService.currentEvent.subscribe(value => {
      this.currentEventItem = value;
      this.isEventInvalid = !isEventBasicDataValid(this.currentEventItem).isValid;
    }))
  }

  getFileFromServer(type: string) {
    this.exportService.downloadStartListFile(type, +this.route.snapshot.queryParams['eventId']).subscribe(
      (blob: Blob) => {
        const formattedDateTime: string = this.getCurrentDateTimeFormatted();
        const filename: string = type + '_' + formattedDateTime + '.' + type;
        const downloadLink: HTMLAnchorElement = this.document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(blob);
        downloadLink.setAttribute('download', filename);
        this.document.body.appendChild(downloadLink);
        downloadLink.click();
      },
      error => {
        console.error('Error downloading file:', error);
      }
    );
  }

  getCurrentDateTimeFormatted(): string {
    const currentDateTime = new Date();
    const year = currentDateTime.getFullYear();
    const month = this.padNumber(currentDateTime.getMonth() + 1, 2);
    const day = this.padNumber(currentDateTime.getDate(), 2);
    return `${year}-${month}-${day}`;
  }

  padNumber(num: number, size: number): string {
    let numStr = num.toString();
    while (numStr.length < size) {
      numStr = '0' + numStr;
    }
    return numStr;
  }

  changeDisabled(item: EventItem | undefined): boolean {
    return item?.status !== EventStatus.INACTIVE && this.authService.isOrganiser();
  }

  handleSearchInput(value: Event): void {
    this.filter.name = (value.target as HTMLInputElement).value
  }

  private subscribeToRouteDataChange(): void {
    this.subscriptions.push(
      this.route.data.subscribe(({type, eventItem, paymentSettings}: Data) => {
        this.componentType = type ?? EventDetailsComponentType.ADD;
        if (eventItem) {
          this.currentEventItem = eventItem;
        } else {
          this.currentEventItem = new EventItem();
          this.currentEventItem.status = EventStatus.INACTIVE;
          this.componentType = EventDetailsComponentType.ADD
        }
        if (paymentSettings) {
          this.currentPaymentSettings = paymentSettings;
        } else {
          this.currentPaymentSettings = new EventPaymentSettings();
        }
        this.isEventInvalid = !isEventBasicDataValid(this.currentEventItem).isValid;
        this.eventService.setCurrentEvent(this.currentEventItem);
      })
    )
  }

  onChangeEventPaymentSettings(paymentSettings: EventPaymentSettings) {
    this.currentPaymentSettings = paymentSettings;
  }

  goToRaceDetails(race: Race): void {
    if (race && race.id) {
      this.raceService.setCurrentRace(race);
      this.router.navigate([this.componentType === EventDetailsComponentType.COPY ? ORGANISER_RACE_EDIT_COPY : ORGANISER_RACE_EDIT], {
        queryParams: {
          eventId: race.eventId,
          raceId: race.id
        }
      });
    }
  }

  goToAddRace(): void {
    if (!(this.componentType === EventDetailsComponentType.COPY)) {
      this.router.navigate([ORGANISER_RACE_ADD], {queryParamsHandling: "preserve"});
    }
  }

  goToRaceManagement(race: Race): void {
    if (race && race.id) {
      this.raceService.setCurrentRace(race);
      this.router.navigate([ORGANISER_RACE_MANAGEMENT], {
        queryParams: {
          eventId: race.eventId,
          raceId: race.id
        }
      });
    }
  }

  goToCopyRace(race: Race): void {
    if (race && race.id) {
      this.router.navigate([ORGANISER_RACE_COPY], {
        queryParams: {
          eventId: race.eventId,
          raceId: race.id,
        }
      });
    }
  }


  handleDropdownClick(event: Event) {
    event.stopPropagation();
  }

  isCopyComponentType(): boolean {
    return this.componentType === EventDetailsComponentType.COPY;
  }

  disableEditing(): boolean {
    return this.isCopyComponentType() || this.isEventInvalid;
  }

  navigateToEventBasicInfo(): void {
    this.nav?.select(1);
  }

  openEraseRaceConfirmationDialog(race: Race) {
    const modalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
    });

    modalRef.componentInstance.text = this.translocoService.translate("event.race.item.list.delete.modal.text");
    modalRef.componentInstance.title = this.translocoService.translate("event.race.item.list.delete.modal.title");
    modalRef.result.then((_) => {
      this.raceService.deleteRace(race, () => {
        this.toastService.showToast(TOAST_STATE.success,
          this.translocoService.translate("event.race.item.list.delete.success"));
        return this.eventService.findEventByIdProtected(new FetchEventByIdRequest(this.currentEventItem?.id), false).subscribe((value: EventItem | null) => {
          this.currentEventItem = value ?? undefined;
        });
      }, () => {
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("event.race.item.list.delete.error"));
      });
    }, (_) => {
      /// ignore
    });
  }
}

export enum EventDetailsComponentType {
  EDIT,
  COPY,
  ADD
}
